'use client';

import type { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';


/**
 * **순위 목록의 작은 표시 조각들** — 라벨과 순위 배지.
 *
 * Research 판매몰 상품 갈래(`/service/research/products`)의 순위 줄을 그린다 — 마켓 화면은
 * `sections/market/components/market-rankings-badges` 의 이름 통로로 부른다.
 */

/**
 * 정적 라벨 — `Chip` 대신 쓴다.
 *
 * 호버도 클릭도 없는 자리에 `Chip`을 쓰면 눌릴 것처럼 보인다. 색은 호출부가 `sx`로 준다.
 */
export function Tag({
  children,
  icon,
  sx,
}: {
  children?: ReactNode;
  icon?: ReactNode;
  sx?: Record<string, unknown>;
}) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        px: 1,
        py: 0.35,
        borderRadius: RADIUS.sm,
        typography: 'uiLabel',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {icon}
      {children}
    </Box>
  );
}

/** 순위 숫자. ⚠️ 1~3위만 강조하고, 순위가 없으면 `–` */
export function RankBadge({ rank, size = 24 }: { rank: number | null; size?: number }) {
  const value = Number(rank);
  const isTop = Number.isFinite(value) && value >= 1 && value <= 3;
  return (
    <Box
      sx={{
        minWidth: size,
        height: size,
        px: 0.5,
        borderRadius: RADIUS.sm,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontWeight: 600,
        fontSize: size >= 28 ? 14 : 12,
        bgcolor: isTop ? 'primary.main' : 'grey.100',
        color: isTop ? 'primary.contrastText' : 'text.secondary',
      }}
    >
      {Number.isFinite(value) ? value : '–'}
    </Box>
  );
}

/**
 * 직전 스냅샷 대비 변동.
 *
 * ⚠️ `change`가 없으면 **아무것도 안 그린다**(`null`). `'same'`이면 `–`를 그린다 —
 * "변동 없음"과 "비교할 것이 없음"은 다른 말이다.
 */
export function RankChangeBadge({
  change,
  delta,
}: {
  change: string | null;
  delta: number | null;
}) {
  if (!change) return null;

  if (change === 'new') {
    return (
      <Typography
        component="span"
        sx={{ typography: 'uiLabel', color: 'info.main', lineHeight: 1, letterSpacing: '-0.02em' }}
      >
        NEW
      </Typography>
    );
  }

  if (change === 'up' || change === 'down') {
    const up = change === 'up';
    return (
      <Typography
        component="span"
        sx={{
          typography: 'uiLabel',
          color: up ? 'success.main' : 'error.main',
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.15,
        }}
      >
        <Iconify icon={up ? 'solar:arrow-up-linear' : 'solar:arrow-down-linear'} width={11} />
        {Math.abs(delta || 0)}
      </Typography>
    );
  }

  return (
    <Typography component="span" sx={{ typography: 'uiLabel', color: 'text.disabled', lineHeight: 1 }}>
      –
    </Typography>
  );
}
